<template lang="pug">
    .floor-plan
        //- Room tabs row with edit controls on the right
        .floor-plan__rooms
            .floor-plan__rooms-left
                el-radio-group(v-model="activeRoomId" size="small")
                    el-radio-button(
                        v-for="room in rooms"
                        :key="room.id"
                        :label="room.id"
                    ) {{ room.name }} ({{ getTableCount(room.id) }})

                .floor-plan__room-actions(v-if="editMode && hasSelectedRoom && (canUpdateFloorPlan || canDeleteFloorPlan)")
                    el-button(
                        v-if="canUpdateFloorPlan"
                        size="small"
                        icon="el-icon-edit"
                        circle
                        @click="renameRoom"
                    )

                    el-button(
                        v-if="canDeleteFloorPlan"
                        size="small"
                        type="danger"
                        icon="el-icon-fa-trash-alt far"
                        circle
                        @click="deleteSelectedRoom"
                    )

                .floor-plan__room-divider(
                    v-if="editMode && canCreateFloorPlan && hasSelectedRoom && (canUpdateFloorPlan || canDeleteFloorPlan)"
                )

                el-button(
                    v-if="editMode && canCreateFloorPlan"
                    size="small"
                    icon="el-icon-plus"
                    circle
                    @click="addRoom"
                )

            .floor-plan__rooms-right
                el-tooltip(:content="editMode ? $t('View Only') : $t('Editing')" placement="bottom")
                    el-button(
                        v-if="canUpdateFloorPlan"
                        size="small"
                        :type="editMode ? 'primary' : 'default'"
                        circle
                        @click="toggleEditMode(!editMode)"
                    )
                        i.el-icon-fa-pencil-alt.fas

                el-dropdown(v-if="editMode && canCreateFloorPlan && !selectionModeActive" @command="addTable" trigger="click")
                    el-button(size="small" type="success" :disabled="!hasSelectedRoom")
                        i.el-icon-plus
                        |  {{ $t('Add Table') }}
                        i.el-icon-arrow-down.el-icon--right
                    el-dropdown-menu(slot="dropdown")
                        el-dropdown-item(command="round")
                            i.el-icon-fa-circle.far
                            |  {{ $t('Round Table') }}
                        el-dropdown-item(command="square")
                            i.el-icon-fa-square.far
                            |  {{ $t('Rectangular Table') }}

                el-dropdown(v-if="editMode && canCreateFloorPlan && !selectionModeActive" @command="addFixture" trigger="click")
                    el-button(size="small" :disabled="!hasSelectedRoom")
                        i.el-icon-plus
                        |  {{ $t('Add object') }}
                        i.el-icon-arrow-down.el-icon--right
                    el-dropdown-menu(slot="dropdown")
                        el-dropdown-item(command="rectangle")
                            span.floor-plan-shape-option
                                svg(viewBox="0 0 16 16" aria-hidden="true")
                                    rect(x="2.7" y="2.7" width="10.6" height="10.6" rx="1.5")
                                span {{ $t('Rectangle') }}
                        el-dropdown-item(command="circle")
                            span.floor-plan-shape-option
                                svg(viewBox="0 0 16 16" aria-hidden="true")
                                    circle(cx="8" cy="8" r="5.3")
                                span {{ $t('Circle') }}
                        el-dropdown-item(command="triangle")
                            span.floor-plan-shape-option
                                svg(viewBox="0 0 16 16" aria-hidden="true")
                                    polygon(points="8,2.7 13.5,13.3 2.5,13.3")
                                span {{ $t('Triangle') }}
                        el-dropdown-item(command="line")
                            span.floor-plan-shape-option
                                svg(viewBox="0 0 16 16" aria-hidden="true")
                                    line(x1="2.5" y1="8" x2="13.5" y2="8")
                                span {{ $t('Divider') }}
                        el-dropdown-item(command="stairs" divided)
                            span.floor-plan-shape-option
                                svg(viewBox="0 0 16 16" aria-hidden="true")
                                    polyline(points="2,13.5 2,10.5 6,10.5 6,7.5 10,7.5 10,4.5 14,4.5 14,2")
                                span {{ $t('Stairs') }}
                        el-dropdown-item(command="spiral-stairs")
                            span.floor-plan-shape-option
                                svg(viewBox="0 0 16 16" aria-hidden="true")
                                    circle(cx="8" cy="8" r="5.8")
                                    line(x1="8" y1="2.2" x2="8" y2="8")
                                    line(x1="8" y1="8" x2="12.7" y2="10.4")
                                    line(x1="8" y1="8" x2="3.3" y2="10.4")
                                span {{ $t('Spiral stairs') }}
                        el-dropdown-item(command="area-link")
                            span.floor-plan-shape-option
                                svg(viewBox="0 0 16 16" aria-hidden="true")
                                    rect(x="1.5" y="2.5" width="9" height="11" rx="1.5" stroke-dasharray="2.5 2")
                                    polyline(points="10,5.5 13.5,8 10,10.5")
                                span {{ $t('Area link') }}

                el-button(
                    v-if="editMode && !selectionModeActive"
                    size="small"
                    @click="startSelectionMode"
                ) {{ $t('Create new combination') }}

                el-button(
                    v-if="showCollapse"
                    size="mini"
                    circle
                    @click="$emit('toggle-collapse')"
                )
                    i(:class="minimizedPanel === 'left' ? 'el-icon-fa-chevron-right fas' : 'el-icon-fa-chevron-left fas'")

        .floor-plan__selection-row(v-if="editMode && selectionModeActive")
            .floor-plan__selection-bar
                .floor-plan__selection-copy
                    span.floor-plan__selection-title {{ $t('Create combination') }}
                    span.floor-plan__selection-count {{ selectionCountLabel }}
                .floor-plan__selection-actions
                    el-button(
                        size="small"
                        @click="cancelSelectionMode"
                    ) {{ $t('Cancel') }}
                    el-button(
                        size="small"
                        type="primary"
                        icon="el-icon-fa-link fas"
                        :disabled="multiSelectedTableIds.length < 2"
                        @click="saveCombinationFromSelection"
                    ) {{ $t('Save combination') }}

        //- Legend row
        .floor-plan__legend-row
            .floor-plan__legend
                .floor-plan__legend-item
                    .floor-plan__legend-dot.floor-plan__legend-dot--available
                    span {{ $t('Available') }}
                .floor-plan__legend-item
                    .floor-plan__legend-dot.floor-plan__legend-dot--reserved
                    span {{ $t('Reserved') }}
                .floor-plan__legend-item
                    .floor-plan__legend-dot.floor-plan__legend-dot--occupied
                    span {{ $t('Occupied') }}
                .floor-plan__legend-item
                    .floor-plan__legend-dot.floor-plan__legend-dot--blocked
                    span {{ $t('Blocked') }}
            .floor-plan__legend-time(v-if="!editMode")
                i.el-icon-time
                |  {{ $t('As of') }} {{ currentTime }} {{ $t('clock') }}

        .floor-plan__selection-hint(v-if="editMode && selectionModeActive")
            | {{ $t('Tap tables to select them for the combination') }}

        //- Main content
        .floor-plan__content
            el-card.floor-plan__canvas-card(shadow="never")
                floor-plan-canvas(
                    :room-id="currentRoom.id"
                    :rooms="rooms"
                    :tables="currentTables"
                    :fixtures="currentFixtures"
                    :selected-fixture-id="selectedFixtureId"
                    :selected-table-id="selectedTableId"
                    :multi-selected-table-ids="multiSelectedTableIds"
                    :selection-mode-active="selectionModeActive"
                    :combination-table-ids="combinationTableIds"
                    :width="currentRoom.width"
                    :height="currentRoom.height"
                    :edit-mode="editMode"
                    :drop-active="dropActive"
                    :table-reservations="tableReservations"
                    :dynamic-statuses="dynamicTableStatuses"
                    @select-table="onSelectTable"
                    @move-table="moveTable"
                    @open-table-details="openTableDetails"
                    @reservation-drop="onReservationDrop"
                    @select-fixture="onSelectFixture"
                    @change-fixture="onChangeFixture"
                    @open-fixture-properties="openFixtureProperties"
                    @delete-fixture="deleteSelectedFixture"
                    @delete-table="deleteSelectedTable"
                    @navigate-room="onNavigateRoom"
                )

        //- Table properties dialog (edit mode)
        el-dialog(
            :visible.sync="tablePropertiesDialogVisible"
            :title="selectedTable ? $t('Table Properties') + ' – Tisch ' + selectedTable.number : $t('Table Properties')"
            width="380px"
            :before-close="closeTablePropertiesDialog"
        )
            table-properties-panel(
                v-if="selectedTable"
                :table="selectedTable"
                :status="selectedTableStatus"
                :can-delete="canDeleteFloorPlan"
                :can-update="canUpdateFloorPlan"
                @update="updateSelectedTable"
                @delete="deleteSelectedTable"
                @block="blockSelectedTable"
                @unblock="unblockSelectedTable"
            )

        //- Object properties dialog (edit mode)
        el-dialog(
            :visible.sync="fixturePropertiesDialogVisible"
            :title="$t('Object properties')"
            width="380px"
            :before-close="closeFixturePropertiesDialog"
        )
            fixture-properties-panel(
                v-if="selectedFixture"
                :fixture="selectedFixture"
                :rooms="rooms"
                :can-delete="canDeleteFloorPlan"
                :can-update="canUpdateFloorPlan"
                @update="updateSelectedFixture"
                @save="saveSelectedFixture"
                @delete="deleteSelectedFixture"
            )

        //- Table timeline dialog (view mode)
        el-dialog(
            :visible.sync="tableTimelineDialogVisible"
            :title="selectedTable ? 'Tisch ' + selectedTable.number : ''"
            width="500px"
            :before-close="closeTableTimelineDialog"
        )
            table-timeline-panel(
                v-if="selectedTable"
                :table="selectedTable"
                :date="reservationStore.filters.date"
                @close="closeTableTimelineDialog"
                @edit-reservation="editReservation"
            )
</template>

<i18n>
{
    "de": {
        "Editing": "Bearbeitung",
        "View Only": "Nur Ansicht",
        "Add Table": "Tisch hinzufügen",
        "Round Table": "Runder Tisch",
        "Rectangular Table": "Eckiger Tisch",
        "Available": "Verfügbar",
        "Reserved": "Reserviert",
        "Occupied": "Besetzt",
        "Blocked": "Blockiert",
        "New Area": "Neuer Bereich",
        "Area Name": "Bereichsname",
        "Rename Area": "Bereich umbenennen",
        "Area renamed": "Bereich umbenannt",
        "Area deleted": "Bereich gelöscht",
        "Delete this area?": "Diesen Bereich löschen?",
        "As of": "Stand",
        "clock": "Uhr",
        "Table Properties": "Tisch-Eigenschaften",
        "Add object": "Objekt hinzufügen",
        "Rectangle": "Rechteck",
        "Circle": "Kreis",
        "Triangle": "Dreieck",
        "Divider": "Trennlinie",
        "Stairs": "Treppe",
        "Spiral stairs": "Wendeltreppe",
        "Area link": "Bereichs-Link",
        "Which area do you want to open?": "Welchen Bereich möchten Sie öffnen?",
        "Object properties": "Objekt-Eigenschaften",
        "Object saved": "Objekt gespeichert",
        "Object deleted": "Objekt gelöscht",
        "Delete this object?": "Dieses Objekt löschen?",
        "Table deleted": "Tisch gelöscht",
        "Delete this table?": "Diesen Tisch löschen?",
        "This action cannot be undone.": "Diese Aktion kann nicht rückgängig gemacht werden.",
        "Select or create a room first": "Wählen Sie zuerst einen Bereich aus oder erstellen Sie einen Bereich.",
        "Save combination": "Kombination speichern",
        "Clear selection": "Auswahl aufheben",
        "Combination name": "Name der Kombination",
        "Combination saved": "Kombination gespeichert",
        "Create new combination": "Neue Kombination anlegen",
        "Create combination": "Kombination anlegen",
        "Tap tables to select them for the combination": "Tische antippen, um sie auszuwählen.",
        "Cancel": "Abbrechen",
        "table selected": "Tisch ausgewählt",
        "tables selected": "Tische ausgewählt",
        "This combination already exists": "Diese Kombination existiert bereits.",
        "Select at least two different tables": "Wählen Sie mindestens zwei unterschiedliche Tische aus."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { format } from 'date-fns';
import { checkRestrictions } from '@/helpers';
import { getTableDisplayStatus } from '@/extensions/tableReservation/store/utils';
import { useTableReservationFloorPlanStore, useTableReservationStore } from '@/stores';
import router from '@/router';
import FloorPlanCanvas from './components/FloorPlanCanvas.vue';
import FixturePropertiesPanel from './components/FixturePropertiesPanel.vue';
import TablePropertiesPanel from './components/TablePropertiesPanel.vue';
import TableTimelinePanel from './components/TableTimelinePanel.vue';

export default {
    name: 'FloorPlan',
    components: {
        FloorPlanCanvas,
        FixturePropertiesPanel,
        TablePropertiesPanel,
        TableTimelinePanel
    },
    props: {
        dropActive: {
            type: Boolean,
            default: false
        },
        showCollapse: {
            type: Boolean,
            default: false
        },
        minimizedPanel: {
            type: String,
            default: null
        }
    },
    data: function () {
        return {
            currentTime: format(new Date(), 'HH:mm'),
            todayDate: format(new Date(), 'yyyy-MM-dd'),
            todayReservations: [],
            timerHandle: null,
            tablePropertiesDialogVisible: false,
            tableTimelineDialogVisible: false,
            fixturePropertiesDialogVisible: false,
            selectionModeActive: false
        };
    },
    watch: {
        selectedTable: function (val) {
            if (!val) {
                this.tablePropertiesDialogVisible = false;
                this.tableTimelineDialogVisible = false;
            }
        },
        selectedFixture: function (val) {
            if (!val) {
                this.fixturePropertiesDialogVisible = false;
            }
        },
        'reservationStore.reservations': function (newVal) {
            if (this.reservationStore.filters.date === this.todayDate) {
                this.todayReservations = newVal.slice();
            }
        }
    },
    mounted: function () {
        this.todayReservations = this.reservationStore.reservations.slice();
        this.timerHandle = setInterval(() => {
            this.currentTime = format(new Date(), 'HH:mm');
        }, 60000);
    },
    beforeDestroy: function () {
        if (this.timerHandle) {
            clearInterval(this.timerHandle);
        }
    },
    computed: {
        store: function () {
            return useTableReservationFloorPlanStore();
        },
        reservationStore: function () {
            return useTableReservationStore();
        },
        rooms: function () {
            return this.store.rooms;
        },
        currentRoom: function () {
            return this.store.currentRoom;
        },
        currentTables: function () {
            return this.store.currentRoomTables;
        },
        currentFixtures: function () {
            return this.store.currentRoomFixtures;
        },
        selectedFixtureId: function () {
            return this.store.selectedFixtureId;
        },
        selectedFixture: function () {
            return this.store.selectedFixture;
        },
        selectedTableId: function () {
            return this.store.selectedTableId;
        },
        selectedTable: function () {
            return this.store.selectedTable;
        },
        multiSelectedTableIds: function () {
            return this.store.selectedTableIds;
        },
        combinationTableIds: function () {
            const ids = {};
            this.store.combinations.forEach((combination) => {
                (combination.tableIds || []).forEach((id) => {
                    ids[String(id)] = true;
                });
            });
            return Object.keys(ids);
        },
        editMode: function () {
            return this.store.editMode;
        },
        activeRoomId: {
            get: function () {
                return this.store.selectedRoomId;
            },
            set: function (roomId) {
                this.store.setSelectedRoom(roomId);
            }
        },
        canCreateFloorPlan: function () {
            return checkRestrictions(['ex:trsv:floor-plan'], 'create');
        },
        canUpdateFloorPlan: function () {
            return checkRestrictions(['ex:trsv:floor-plan'], 'update');
        },
        canDeleteFloorPlan: function () {
            return checkRestrictions(['ex:trsv:floor-plan'], 'delete');
        },
        dynamicTableStatuses: function () {
            const result = {};
            this.store.tables.forEach((table) => {
                result[table.id] = getTableDisplayStatus(
                    table,
                    this.todayReservations,
                    this.todayDate,
                    this.currentTime
                );
            });
            return result;
        },
        selectedTableStatus: function () {
            if (!this.selectedTable) {
                return 'available';
            }

            return this.dynamicTableStatuses[this.selectedTable.id] || 'available';
        },
        hasSelectedRoom: function () {
            return !!this.store.selectedRoomId;
        },
        selectionCountLabel: function () {
            const count = this.multiSelectedTableIds.length;
            return count + ' ' + this.$t(count === 1 ? 'table selected' : 'tables selected');
        },
        tableReservations: function () {
            // Map of tableId -> reservation info for the current filter date
            const filterDate = this.reservationStore.filters.date;
            const reservations = this.reservationStore.reservations;
            const result = {};
            reservations.forEach((r) => {
                if (r.date === filterDate && r.status !== 'cancelled' && r.status !== 'no_show' && r.status !== 'completed') {
                    (r.tableIds || []).forEach((tid) => {
                        if (!result[tid] || r.time < result[tid].time) {
                            result[tid] = {
                                guestName: r.guestName,
                                time: r.time,
                                partySize: r.partySize,
                                status: r.status
                            };
                        }
                    });
                }
            });
            return result;
        }
    },
    methods: {
        showActionError: function (error) {
            this.$message({
                message: error.message || this.$t('Something went wrong'),
                type: 'error',
                duration: 5000
            });
        },
        selectTable: function (tableId) {
            if (tableId) {
                this.store.selectTable(tableId);
            } else {
                this.store.deselectTable();
            }
        },
        onSelectTable: function (payload) {
            // payload may be string id (legacy) or { id, additive }
            if (payload && typeof payload === 'object') {
                if (this.editMode && payload.id && payload.additive) {
                    this.store.toggleTableSelection(payload.id, true);
                    return;
                }
                this.store.clearTableSelection();
                this.selectTable(payload.id || null);
                return;
            }
            this.store.clearTableSelection();
            this.selectTable(payload);
        },
        clearMultiSelection: function () {
            this.store.clearTableSelection();
        },
        startSelectionMode: function () {
            this.selectionModeActive = true;
            this.store.clearTableSelection();
            this.store.deselectTable();
            this.tablePropertiesDialogVisible = false;
            this.tableTimelineDialogVisible = false;
        },
        cancelSelectionMode: function () {
            this.selectionModeActive = false;
            this.store.clearTableSelection();
        },
        saveCombinationFromSelection: function () {
            const ids = this.store.prepareCombinationTableIds(this.store.selectedTableIds || []);
            if (ids.length < 2) {
                this.$message({
                    message: this.$t('Select at least two different tables'),
                    type: 'warning'
                });
                return;
            }

            if (this.store.findExistingCombinationByTableIds(ids)) {
                this.$message({
                    message: this.$t('This combination already exists'),
                    type: 'warning'
                });
                return;
            }

            const defaultName = ids
                .map((id) => {
                    const table = this.store.tables.find((t) => { return t.id === id; });
                    return table ? table.name : '';
                })
                .filter(Boolean)
                .join(' + ');

            this.$prompt(this.$t('Combination name'), this.$t('Save combination'), {
                confirmButtonText: this.$t('OK'),
                cancelButtonText: this.$t('Cancel'),
                inputValue: defaultName
            }).then((result) => {
                return this.store.createCombination({
                    name: result.value || defaultName,
                    tableIds: ids
                });
            }).then(() => {
                this.$message({ message: this.$t('Combination saved'), type: 'success' });
                this.selectionModeActive = false;
                this.store.clearTableSelection();
            }).catch((error) => {
                if (error === 'cancel' || error === 'close') {
                    return;
                }
                this.showActionError(error);
            });
        },
        openTableDetails: function (tableId) {
            if (!tableId) return;
            this.store.selectTable(tableId);
            if (this.editMode) {
                this.tableTimelineDialogVisible = false;
                this.tablePropertiesDialogVisible = true;
            } else {
                this.tablePropertiesDialogVisible = false;
                this.tableTimelineDialogVisible = true;
            }
        },
        moveTable: async function (payload) {
            try {
                await this.store.moveTable(payload.id, payload.x, payload.y);
            } catch (error) {
                this.showActionError(error);
            }
        },
        addTable: async function (shape) {
            try {
                await this.store.addTable(this.store.selectedRoomId, { shape: shape });
            } catch (error) {
                this.showActionError(error);
            }
        },
        addFixture: async function (type) {
            try {
                const fixture = await this.store.addFixture(this.store.selectedRoomId, { type: type });
                this.openFixtureProperties(fixture.id);
            } catch (error) {
                this.showActionError(error);
            }
        },
        findRoom: function (roomId) {
            if (!roomId) {
                return null;
            }

            return this.rooms.find((room) => { return room.id === roomId; }) || null;
        },
        onNavigateRoom: function (payload) {
            const upRoom = this.findRoom(_.get(payload, 'up'));
            const downRoom = this.findRoom(_.get(payload, 'down'));

            if (upRoom && downRoom && upRoom.id !== downRoom.id) {
                this.$confirm(this.$t('Which area do you want to open?'), this.$t('Stairs'), {
                    confirmButtonText: upRoom.name,
                    cancelButtonText: downRoom.name,
                    distinguishCancelAndClose: true,
                    type: 'info'
                }).then(() => {
                    this.openRoom(upRoom.id);
                }).catch((action) => {
                    if (action === 'cancel') {
                        this.openRoom(downRoom.id);
                    }
                });
                return;
            }

            const targetRoom = upRoom || downRoom;

            if (targetRoom) {
                this.openRoom(targetRoom.id);
            }
        },
        openRoom: function (roomId) {
            this.store.deselectTable();
            this.store.deselectFixture();
            this.store.setSelectedRoom(roomId);
        },
        onSelectFixture: function (fixtureId) {
            if (fixtureId) {
                this.store.selectFixture(fixtureId);
            } else {
                this.store.deselectFixture();
            }
        },
        onChangeFixture: async function (payload) {
            try {
                await this.store.updateFixture(payload.id, payload.geometry);
            } catch (error) {
                this.showActionError(error);
            }
        },
        openFixtureProperties: function (fixtureId) {
            if (!fixtureId || !this.editMode) {
                return;
            }

            this.store.selectFixture(fixtureId);
            this.fixturePropertiesDialogVisible = true;
        },
        closeFixturePropertiesDialog: function () {
            this.fixturePropertiesDialogVisible = false;
            this.store.deselectFixture();
        },
        updateSelectedFixture: async function (data) {
            if (!this.selectedFixtureId) {
                return;
            }

            try {
                await this.store.updateFixture(this.selectedFixtureId, data);
            } catch (error) {
                this.showActionError(error);
            }
        },
        saveSelectedFixture: async function (data) {
            if (!this.selectedFixtureId) {
                return;
            }

            try {
                await this.store.updateFixture(this.selectedFixtureId, data);
                this.closeFixturePropertiesDialog();
                this.$message({ message: this.$t('Object saved'), type: 'success' });
            } catch (error) {
                this.showActionError(error);
            }
        },
        deleteSelectedFixture: function () {
            const fixtureId = this.selectedFixtureId;

            if (!fixtureId) {
                return;
            }

            this.$confirm(
                this.$t('This action cannot be undone.'),
                this.$t('Delete this object?'),
                {
                    confirmButtonText: this.$t('OK'),
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning'
                }
            ).then(() => {
                this.store.deleteFixture(fixtureId).then(() => {
                    this.fixturePropertiesDialogVisible = false;
                    this.$message({ message: this.$t('Object deleted'), type: 'success' });
                }).catch((error) => {
                    this.showActionError(error);
                });
            }).catch(() => {});
        },
        updateSelectedTable: async function (data) {
            if (this.selectedTableId) {
                try {
                    await this.store.updateTable(this.selectedTableId, data);
                } catch (error) {
                    this.showActionError(error);
                }
            }
        },
        deleteSelectedTable: function () {
            this.$confirm(
                this.$t('This action cannot be undone.'),
                this.$t('Delete this table?'),
                {
                    confirmButtonText: this.$t('OK'),
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning'
                }
            ).then(() => {
                this.store.deleteTable(this.selectedTableId).then(() => {
                    this.tablePropertiesDialogVisible = false;
                    this.$message({ message: this.$t('Table deleted'), type: 'success' });
                }).catch((error) => {
                    this.showActionError(error);
                });
            }).catch(() => {});
        },
        blockSelectedTable: async function () {
            if (this.selectedTableId) {
                try {
                    await this.store.blockTable(this.selectedTableId);
                } catch (error) {
                    this.showActionError(error);
                }
            }
        },
        unblockSelectedTable: async function () {
            if (this.selectedTableId) {
                try {
                    await this.store.unblockTable(this.selectedTableId);
                } catch (error) {
                    this.showActionError(error);
                }
            }
        },
        toggleEditMode: function (value) {
            if (!this.canUpdateFloorPlan) return;
            this.store.setEditMode(value);
            if (!value) {
                this.selectionModeActive = false;
                this.store.clearTableSelection();
            }
        },
        getTableCount: function (roomId) {
            return this.store.tablesByRoom(roomId).length;
        },
        addRoom: function () {
            this.$prompt(this.$t('Area Name'), this.$t('New Area'), {
                confirmButtonText: this.$t('OK'),
                cancelButtonText: this.$t('Cancel'),
                inputValue: 'Neuer Bereich'
            }).then((result) => {
                this.store.addRoom({ name: result.value }).catch((error) => {
                    this.showActionError(error);
                });
            }).catch(() => {});
        },
        renameRoom: function () {
            if (!this.currentRoom || !this.currentRoom.id) return;

            this.$prompt(this.$t('Area Name'), this.$t('Rename Area'), {
                confirmButtonText: this.$t('OK'),
                cancelButtonText: this.$t('Cancel'),
                inputValue: this.currentRoom.name
            }).then((result) => {
                this.store.updateRoom(this.currentRoom.id, { name: result.value }).then(() => {
                    this.$message({ message: this.$t('Area renamed'), type: 'success' });
                }).catch((error) => {
                    this.showActionError(error);
                });
            }).catch(() => {});
        },
        deleteSelectedRoom: function () {
            if (!this.currentRoom || !this.currentRoom.id) return;

            this.$confirm(
                this.$t('This action cannot be undone.'),
                this.$t('Delete this area?'),
                {
                    confirmButtonText: this.$t('OK'),
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning'
                }
            ).then(() => {
                this.store.deleteRoom(this.currentRoom.id).then(() => {
                    this.$message({ message: this.$t('Area deleted'), type: 'success' });
                }).catch((error) => {
                    this.showActionError(error);
                });
            }).catch(() => {});
        },
        onReservationDrop: function (payload) {
            this.$emit('reservation-drop', payload);
        },
        closeTablePropertiesDialog: function () {
            this.tablePropertiesDialogVisible = false;
            this.store.deselectTable();
        },
        closeTableTimelineDialog: function () {
            this.tableTimelineDialogVisible = false;
            this.store.deselectTable();
        },
        editReservation: function (res) {
            this.closeTableTimelineDialog();
            router.push({ name: 'table-reservation-form', params: { id: res.id } });
        }
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.floor-plan-shape-option
    display: inline-flex
    align-items: center
    gap: 8px

    > svg
        flex: 0 0 16px
        width: 16px
        height: 16px
        fill: none
        stroke: currentColor
        stroke-width: 1.4
        stroke-linejoin: round
        stroke-linecap: round

.floor-plan
    display: flex
    flex-direction: column
    height: 100%
    min-height: 0

    &__rooms
        margin-bottom: 15px
        display: flex
        align-items: center
        justify-content: space-between
        flex-wrap: wrap
        gap: 8px

        &-left
            display: flex
            align-items: center
            gap: 8px

        &-right
            display: flex
            align-items: center
            flex-wrap: wrap
            gap: 8px

    &__selection-row
        margin-bottom: 12px

    &__selection-hint
        font-size: 0.85rem
        color: #909399
        line-height: 1.3
        margin-bottom: 12px

    &__selection-bar
        display: flex
        align-items: center
        gap: 12px
        flex-wrap: wrap
        padding: 6px 10px
        border: 1px solid #ebeef5
        border-radius: 6px
        background: #f8fafc

    &__selection-copy
        display: flex
        align-items: center
        gap: 10px
        flex-wrap: wrap

    &__selection-title
        font-weight: 600
        color: #303133

    &__selection-count
        font-size: 0.85rem
        color: #909399

    &__selection-actions
        display: flex
        align-items: center
        gap: 8px

    &__room-actions
        display: flex
        align-items: center
        gap: 0

    &__room-divider
        width: 1px
        height: 22px
        background: #dcdfe6
        margin: 0 2px

    &__legend-row
        display: flex
        flex-wrap: wrap
        justify-content: space-between
        align-items: center
        margin-bottom: 15px
        gap: 8px

    &__legend
        display: flex
        flex-wrap: wrap
        gap: 10px

        &-time
            font-size: 0.75rem
            color: #909399

        &-item
            display: flex
            align-items: center
            gap: 4px
            font-size: 0.8rem
            color: #606266
            white-space: nowrap

        &-dot
            width: 12px
            height: 12px
            border-radius: 50%
            border: 1px solid

            &--available
                background: #f0f9eb
                border-color: #67c23a

            &--reserved
                background: #fdf6ec
                border-color: #e6a23c

            &--occupied
                background: #fef0f0
                border-color: #f56c6c

            &--blocked
                background: #e9e9eb
                border-color: #909399

    &__content
        flex: 1 1 auto
        min-height: 0
        display: flex

    &__canvas-card
        border: none
        flex: 1 1 auto
        min-width: 0
        display: flex
        flex-direction: column

        &::v-deep .el-card__body
            flex: 1 1 auto
            min-height: 0
            overflow: auto
            padding: 0
            display: flex
</style>
